import { Download, Sparkles, X } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import type { ExportExpansionColumn } from "@signum/api-contracts";
import { useAuth } from "../lib/auth.js";
import { ApiClient } from "../lib/api.js";

const groups: Array<{title: string; description: string; items: Array<{
  value: ExportExpansionColumn; label: string;
}>}> = [
  {
    title: "Trade and fundamentals",
    description: "Purchase detail and point-in-time company financials.",
    items: [
      {value: "weighted_average_purchase_price", label: "Weighted purchase price"},
      {value: "share_price", label: "Share price at signal date"},
      {value: "enterprise_value", label: "Enterprise value"},
      {value: "ebitda_ttm", label: "EBITDA (TTM)"},
      {value: "free_cash_flow_growth", label: "Free cash flow growth"},
      {value: "total_debt", label: "Total debt"},
      {value: "stockholders_equity", label: "Stockholders’ equity"},
    ],
  },
  {
    title: "Historical price factors",
    description: "Calculated from adjusted daily closing prices.",
    items: [
      {value: "distance_from_52_week_high", label: "Distance from 52-week high"},
      {value: "return_1_month_before", label: "1-month trailing return"},
      {value: "return_3_month_before", label: "3-month trailing return"},
    ],
  },
  {
    title: "Outcome labels",
    description: "Forward performance for research, never policy inputs.",
    items: [
      {value: "forward_return_3_month", label: "3-month forward return"},
      {value: "forward_return_6_month", label: "6-month forward return"},
      {value: "forward_return_12_month", label: "12-month forward return"},
      {value: "excess_return_3_month_vs_sp500", label: "3-month excess vs S&P 500"},
      {value: "excess_return_6_month_vs_sp500", label: "6-month excess vs S&P 500"},
      {value: "excess_return_12_month_vs_sp500", label: "12-month excess vs S&P 500"},
    ],
  },
];
const allExpansions = groups.flatMap((group) => group.items.map((item) => item.value));

export function CandidateExportDialog({open, startDate, endDate, activeExpandedExport, onClose, onJobCreated}: {
  open: boolean;
  startDate: string;
  endDate: string;
  activeExpandedExport: boolean;
  onClose(): void;
  onJobCreated(): void;
}) {
  const {accessToken} = useAuth();
  const [start, setStart] = useState(startDate);
  const [end, setEnd] = useState(endDate);
  const [scope, setScope] = useState<"all" | "recommended">("all");
  const [direction, setDirection] = useState<"asc" | "desc">("desc");
  const [selected, setSelected] = useState<ExportExpansionColumn[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [queuedStandard,setQueuedStandard]=useState<boolean|undefined>();

  useEffect(() => {
    if (!open) return;
    setStart(startDate); setEnd(endDate); setError("");
    let stopped=false;
    new ApiClient(accessToken).exportCapabilities().then((value) => {if(!stopped)setQueuedStandard(value.queuedStandardExport);})
      .catch((caught) => {if(!stopped)setError(caught instanceof Error?caught.message:String(caught));});
    return () => {stopped=true;};
  }, [open, startDate, endDate,accessToken]);

  if (!open) return null;

  async function submit(event: FormEvent) {
    event.preventDefault(); setSubmitting(true); setError("");
    try {
      const api = new ApiClient(accessToken);
      if (selected.length || queuedStandard) {
        await api.createExportJob({
          startDate: start, endDate: end, scope, direction, expansionColumns: selected,
        });
        onJobCreated();
      } else {
        const result = await api.candidateExport({
          startDate: start, endDate: end, scope, direction,
        });
        const url = URL.createObjectURL(result.blob);
        const link = document.createElement("a");
        link.href = url; link.download = result.filename; link.hidden = true;
        document.body.append(link); link.click(); link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
      }
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setSubmitting(false);
    }
  }

  function toggle(value: ExportExpansionColumn) {
    setSelected((current) => current.includes(value)
      ? current.filter((item) => item !== value) : [...current, value]);
  }

  const expandedBlocked = (selected.length > 0 || queuedStandard) && activeExpandedExport;
  return <div className="modal-backdrop" role="presentation"><section className="modal export-modal" role="dialog" aria-modal="true" aria-labelledby="export-title">
    <div className="modal-head"><div><span className="eyebrow">Candidate dataset</span><h2 id="export-title">Export candidate data</h2></div><button className="icon-button" onClick={onClose} aria-label="Close export"><X size={19}/></button></div>
    <form onSubmit={submit}>
      <p className="export-note">{queuedStandard ? "All exports use one frozen dataset release and run in the background. Download the completed CSV from Recent exports; no external financial API is called." : "The standard export downloads immediately from persisted data. Select expanded columns to prepare an enriched, reusable dataset."}</p>
      <div className="export-grid">
        <label>Start date<input type="date" required value={start} max={end || undefined} onChange={(event) => setStart(event.target.value)}/></label>
        <label>End date<input type="date" required value={end} min={start || undefined} onChange={(event) => setEnd(event.target.value)}/></label>
        <label>Candidates<select value={scope} onChange={(event) => setScope(event.target.value as typeof scope)}><option value="all">All candidates</option><option value="recommended">Recommendations only</option></select></label>
        <label>Analysis date order<select value={direction} onChange={(event) => setDirection(event.target.value as typeof direction)}><option value="desc">Newest first</option><option value="asc">Oldest first</option></select></label>
      </div>
      <div className="expansion-heading"><div><span className="eyebrow">Optional data expansion</span><h3>Expanded columns</h3></div><button type="button" className="text-button" onClick={() => setSelected(selected.length === allExpansions.length ? [] : allExpansions)}>{selected.length === allExpansions.length ? "Clear all" : "Select all"}</button></div>
      <div className="expansion-groups">{groups.map((group) => <fieldset className="expansion-group" key={group.title}>
        <legend>{group.title}</legend><p>{group.description}</p>
        <div>{group.items.map((item) => <label className="check-option" key={item.value}><input type="checkbox" checked={selected.includes(item.value)} onChange={() => toggle(item.value)}/><span>{item.label}</span></label>)}</div>
      </fieldset>)}</div>
      {expandedBlocked && <p className="export-active-note">{queuedStandard ? "An export is already in progress. Please wait for it to finish or cancel it in Recent exports." : "An expanded export is already in progress. You can still clear these selections and download the standard export now."}</p>}
      {error && <p className="form-error">{error}</p>}
      <div className="modal-actions"><button type="button" className="button secondary" onClick={onClose}>Cancel</button><button className="button primary" disabled={submitting || Boolean(expandedBlocked) || queuedStandard===undefined || !start || !end || start > end}>{selected.length ? <Sparkles size={16}/> : <Download size={16}/>} {submitting ? "Preparing…" : selected.length ? "Prepare expanded export" : queuedStandard ? "Prepare CSV" : "Download CSV"}</button></div>
    </form>
  </section></div>;
}
